// Gemini API paid-tier prices (USD), copied from https://ai.google.dev/gemini-api/docs/pricing on 2026-10-01.
// Only used to seed Settings > Gemini Pricing; the saved copy in the backend is the source of truth.

export const GEMINI_PRICING_SOURCE = 'https://ai.google.dev/gemini-api/docs/pricing'
export const GEMINI_PRICING_FETCHED_AT = '2026-10-01'

// Date Google's announced increases for the newer models take effect
const NEXT = '2027-01-01'

export const PRICE_KINDS = [
  { value: 'input', label: 'Input' },
  { value: 'output', label: 'Output' },
  { value: 'cache', label: 'Context caching' },
  { value: 'storage', label: 'Cache storage' },
]
export const PRICE_MODES = [
  { value: 'standard', label: 'Standard' },
  { value: 'batch', label: 'Batch' },
]
export const PRICE_CONDITIONS = [
  { value: 'all', label: 'Any prompt size' },
  { value: 'lte200k', label: 'Prompt ≤ 200k tokens' },
  { value: 'gt200k', label: 'Prompt > 200k tokens' },
]
export const PRICE_UNITS = [
  { value: 'tokens', label: 'per 1M tokens' },
  { value: 'tokens_hour', label: 'per 1M tokens / hour' },
  { value: 'minute', label: 'per minute' },
  { value: 'second', label: 'per second' },
  { value: 'image', label: 'per image' },
  { value: 'frame', label: 'per frame' },
  { value: 'song', label: 'per song' },
]
export const MODEL_CATEGORIES = [
  { value: 'text', label: 'Text / multimodal' },
  { value: 'tts', label: 'Text to speech' },
  { value: 'live', label: 'Live / realtime' },
  { value: 'image', label: 'Image generation' },
  { value: 'video', label: 'Video generation' },
  { value: 'music', label: 'Music' },
  { value: 'embedding', label: 'Embedding' },
  { value: 'robotics', label: 'Robotics' },
]
export const MODEL_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'preview', label: 'Preview' },
  { value: 'deprecated', label: 'Deprecated' },
]

/**
 * One price line. `nextPrice` replaces `price` from `nextPriceFrom` (YYYY-MM-DD).
 * modality: 'text' | 'text/image/video' | 'all' (text/image/video/audio) | 'audio' | 'image' | 'video' | 'image/video'
 */
const p = (kind, modality, mode, price, unit = 'tokens', extra = {}) => ({
  kind, modality, mode, price, unit, condition: 'all', nextPrice: null, nextPriceFrom: null, note: '', ...extra,
})
const later = (nextPrice) => ({ nextPrice, nextPriceFrom: NEXT })
const small = { condition: 'lte200k' }
const large = { condition: 'gt200k' }

const model = (modelId, name, category, freeTier, prices, extra = {}) => ({
  modelId, name, category, freeTier, status: 'active', note: '', prices, ...extra,
})

export const GEMINI_DEFAULT_MODELS = [
  // Gemini 3.8
  model('gemini-3.8-flash', 'Gemini 3.8 Flash', 'text', true, [
    p('input', 'all', 'standard', 0.75, 'tokens', later(1.5)),
    p('output', 'text', 'standard', 3.75, 'tokens', { ...later(7.5), note: 'Includes thinking tokens' }),
    p('cache', 'all', 'standard', 0.075, 'tokens', later(0.15)),
    p('storage', 'all', 'standard', 0.5, 'tokens_hour', later(1)),
    p('input', 'all', 'batch', 0.375, 'tokens', later(0.75)),
    p('output', 'text', 'batch', 1.875, 'tokens', later(3.75)),
  ]),
  model('gemini-3.8-flash-tts', 'Gemini 3.8 Flash TTS', 'tts', true, [
    p('input', 'text', 'standard', 0.5, 'tokens', later(1)),
    p('output', 'audio', 'standard', 9, 'tokens', later(18)),
  ]),
  model('gemini-3.8-flash-lite-tts', 'Gemini 3.8 Flash-Lite TTS', 'tts', true, [
    p('input', 'text', 'standard', 0.5, 'tokens', later(1)),
    p('output', 'audio', 'standard', 6, 'tokens', later(12)),
    p('cache', 'text', 'standard', 0.125, 'tokens', later(0.25)),
    p('storage', 'text', 'standard', 0.5, 'tokens_hour', later(1)),
    p('input', 'text', 'batch', 0.25, 'tokens', later(0.5)),
    p('output', 'audio', 'batch', 3, 'tokens', later(6)),
    p('cache', 'text', 'batch', 0.0625, 'tokens', later(0.125)),
  ]),
  model('gemini-3.8-live', 'Gemini 3.8 Live', 'live', true, [
    p('input', 'text', 'standard', 0.75),
    p('input', 'audio', 'standard', 3, 'tokens', { note: '≈ $0.005 / minute' }),
    p('input', 'image/video', 'standard', 1, 'tokens', { note: '≈ $0.002 / minute' }),
    p('output', 'text', 'standard', 4.5),
    p('output', 'audio', 'standard', 12, 'tokens', { note: '≈ $0.018 / minute' }),
  ]),

  // Gemini 3.x
  model('gemini-3.7-flash', 'Gemini 3.7 Flash', 'text', true, [
    p('input', 'all', 'standard', 0.75, 'tokens', later(1.5)),
    p('output', 'text', 'standard', 3.75, 'tokens', later(7.5)),
    p('cache', 'all', 'standard', 0.075, 'tokens', later(0.15)),
    p('input', 'all', 'batch', 0.375, 'tokens', later(0.75)),
    p('output', 'text', 'batch', 1.875, 'tokens', later(3.75)),
  ]),
  model('gemini-3.6-flash', 'Gemini 3.6 Flash', 'text', true, [
    p('input', 'all', 'standard', 0.75, 'tokens', later(1.5)),
    p('output', 'text', 'standard', 3.75, 'tokens', later(7.5)),
    p('input', 'all', 'batch', 0.375, 'tokens', later(0.75)),
    p('output', 'text', 'batch', 1.875, 'tokens', later(3.75)),
  ]),
  model('gemini-3.5-flash', 'Gemini 3.5 Flash', 'text', true, [
    p('input', 'all', 'standard', 1.5),
    p('output', 'text', 'standard', 9),
    p('cache', 'all', 'standard', 0.15),
    p('storage', 'all', 'standard', 1, 'tokens_hour'),
    p('input', 'all', 'batch', 0.75),
    p('output', 'text', 'batch', 4.5),
    p('cache', 'all', 'batch', 0.075),
  ]),
  model('gemini-3.5-flash-lite', 'Gemini 3.5 Flash-Lite', 'text', true, [
    p('input', 'all', 'standard', 0.3),
    p('output', 'text', 'standard', 2.5),
    p('input', 'all', 'batch', 0.15),
    p('output', 'text', 'batch', 1.25),
  ]),
  model('gemini-3.1-flash-lite', 'Gemini 3.1 Flash-Lite', 'text', true, [
    p('input', 'text/image/video', 'standard', 0.25),
    p('input', 'audio', 'standard', 0.5),
    p('output', 'text', 'standard', 1.5),
    p('cache', 'text/image/video', 'standard', 0.025),
    p('cache', 'audio', 'standard', 0.05),
    p('storage', 'all', 'standard', 1, 'tokens_hour'),
    p('input', 'text/image/video', 'batch', 0.125),
    p('input', 'audio', 'batch', 0.25),
    p('output', 'text', 'batch', 0.75),
    p('cache', 'text/image/video', 'batch', 0.0125),
    p('cache', 'audio', 'batch', 0.025),
    p('storage', 'all', 'batch', 0.5, 'tokens_hour'),
  ]),
  model('gemini-3.1-pro-preview', 'Gemini 3.1 Pro Preview', 'text', false, [
    p('input', 'all', 'standard', 2, 'tokens', small),
    p('input', 'all', 'standard', 4, 'tokens', large),
    p('output', 'text', 'standard', 12, 'tokens', small),
    p('output', 'text', 'standard', 18, 'tokens', large),
    p('cache', 'all', 'standard', 0.2, 'tokens', small),
    p('cache', 'all', 'standard', 0.4, 'tokens', large),
    p('storage', 'all', 'standard', 4.5, 'tokens_hour'),
    p('input', 'all', 'batch', 1, 'tokens', small),
    p('input', 'all', 'batch', 2, 'tokens', large),
    p('output', 'text', 'batch', 6, 'tokens', small),
    p('output', 'text', 'batch', 9, 'tokens', large),
  ], { status: 'preview' }),
  model('gemini-3.1-flash-image', 'Gemini 3.1 Flash Image', 'image', false, [
    p('input', 'text/image', 'standard', 0.5),
    p('output', 'text', 'standard', 3),
    p('output', 'image', 'standard', 60),
    p('input', 'text/image', 'batch', 0.25),
    p('output', 'text', 'batch', 1.5),
    p('output', 'image', 'batch', 30),
  ]),
  model('gemini-3.1-flash-lite-image', 'Gemini 3.1 Flash Lite Image', 'image', false, [
    p('input', 'text/image', 'standard', 0.25),
    p('output', 'text', 'standard', 1.5),
    p('output', 'image', 'standard', 30),
    p('input', 'text/image', 'batch', 0.125),
    p('output', 'text', 'batch', 0.75),
    p('output', 'image', 'batch', 15),
  ]),
  model('gemini-3-flash-preview', 'Gemini 3 Flash Preview', 'text', true, [
    p('input', 'text/image/video', 'standard', 0.5),
    p('input', 'audio', 'standard', 1),
    p('output', 'text', 'standard', 3),
    p('cache', 'text/image/video', 'standard', 0.05),
    p('cache', 'audio', 'standard', 0.1),
    p('storage', 'all', 'standard', 1, 'tokens_hour'),
    p('input', 'text/image/video', 'batch', 0.25),
    p('input', 'audio', 'batch', 0.5),
    p('output', 'text', 'batch', 1.5),
  ], { status: 'preview' }),
  model('gemini-3-pro-image', 'Gemini 3 Pro Image', 'image', false, [
    p('input', 'text/image', 'standard', 2, 'tokens', { note: '≈ $0.0011 per input image' }),
    p('output', 'text', 'standard', 12),
    p('output', 'image', 'standard', 120, 'tokens', { note: '≈ $0.134 per 1K/2K image, $0.24 per 4K' }),
    p('input', 'text', 'batch', 1),
    p('output', 'text', 'batch', 6),
    p('output', 'image', 'batch', 60, 'tokens', { note: '≈ $0.067 per 1K/2K image, $0.12 per 4K' }),
  ]),

  // Gemini 2.5
  model('gemini-2.5-pro', 'Gemini 2.5 Pro', 'text', true, [
    p('input', 'all', 'standard', 1.25, 'tokens', small),
    p('input', 'all', 'standard', 2.5, 'tokens', large),
    p('output', 'text', 'standard', 10, 'tokens', small),
    p('output', 'text', 'standard', 15, 'tokens', large),
    p('cache', 'all', 'standard', 0.125, 'tokens', small),
    p('cache', 'all', 'standard', 0.25, 'tokens', large),
    p('storage', 'all', 'standard', 4.5, 'tokens_hour'),
    p('input', 'all', 'batch', 0.625, 'tokens', small),
    p('input', 'all', 'batch', 1.25, 'tokens', large),
    p('output', 'text', 'batch', 5, 'tokens', small),
    p('output', 'text', 'batch', 7.5, 'tokens', large),
  ]),
  model('gemini-2.5-flash', 'Gemini 2.5 Flash', 'text', true, [
    p('input', 'text/image/video', 'standard', 0.3),
    p('input', 'audio', 'standard', 1),
    p('output', 'text', 'standard', 2.5),
    p('cache', 'text/image/video', 'standard', 0.03),
    p('cache', 'audio', 'standard', 0.1),
    p('storage', 'all', 'standard', 1, 'tokens_hour'),
    p('input', 'text/image/video', 'batch', 0.15),
    p('input', 'audio', 'batch', 0.5),
    p('output', 'text', 'batch', 1.25),
  ]),
  model('gemini-2.5-flash-lite', 'Gemini 2.5 Flash-Lite', 'text', true, [
    p('input', 'text/image/video', 'standard', 0.1),
    p('input', 'audio', 'standard', 0.3),
    p('output', 'text', 'standard', 0.4),
    p('cache', 'text/image/video', 'standard', 0.01),
    p('cache', 'audio', 'standard', 0.03),
    p('storage', 'all', 'standard', 1, 'tokens_hour'),
    p('input', 'text/image/video', 'batch', 0.05),
    p('input', 'audio', 'batch', 0.15),
    p('output', 'text', 'batch', 0.2),
  ]),
  model('gemini-2.5-flash-native-audio-preview-12-2025', 'Gemini 2.5 Flash Native Audio (Live API)', 'live', true, [
    p('input', 'text', 'standard', 0.5),
    p('input', 'audio/video', 'standard', 3),
    p('output', 'text', 'standard', 2),
    p('output', 'audio', 'standard', 12),
  ], { status: 'preview' }),
  model('gemini-2.5-flash-image', 'Gemini 2.5 Flash Image', 'image', false, [
    p('input', 'text/image', 'standard', 0.3),
    p('output', 'image', 'standard', 0.039, 'image'),
    p('input', 'text/image', 'batch', 0.15),
    p('output', 'image', 'batch', 0.0195, 'image'),
  ], { status: 'deprecated', note: 'Retiring October 2, 2026' }),

  // Specialized
  model('gemini-omni-1.1-flash', 'Gemini Omni Flash', 'video', false, [
    p('input', 'all', 'standard', 1.5),
    p('output', 'text', 'standard', 9),
    p('output', 'video', 'standard', 17.5, 'tokens', { note: '≈ $0.10 / second at 720p' }),
  ]),
  model('gemini-3.5-live-translate-preview', 'Gemini 3.5 Live Translate', 'live', true, [
    p('input', 'audio', 'standard', 3.5, 'tokens', { note: '≈ $0.0053 / minute' }),
    p('output', 'audio', 'standard', 21, 'tokens', { note: '≈ $0.0315 / minute' }),
  ], { status: 'preview' }),
  model('gemini-3.5-transcribe-live', 'Gemini 3.5 Transcribe Live', 'live', true, [
    p('input', 'audio', 'standard', 3.5, 'tokens', { note: '≈ $0.005 / minute' }),
    p('output', 'text', 'standard', 21, 'tokens', { note: '≈ $0.004 / minute' }),
  ]),
  model('gemini-embedding-2', 'Gemini Embedding 2', 'embedding', true, [
    p('input', 'text', 'standard', 0.2),
    p('input', 'image', 'standard', 0.45, 'tokens', { note: '≈ $0.00012 per image' }),
    p('input', 'audio', 'standard', 6.5, 'tokens', { note: '≈ $0.00016 per second' }),
    p('input', 'video', 'standard', 12, 'tokens', { note: '≈ $0.00079 per frame' }),
    p('input', 'text', 'batch', 0.1),
    p('input', 'image', 'batch', 0.225),
    p('input', 'audio', 'batch', 3.25),
    p('input', 'video', 'batch', 6),
  ]),
  model('gemini-robotics-er-2-preview', 'Gemini Robotics ER 2 Preview', 'robotics', true, [
    p('input', 'all', 'standard', 1, 'tokens', later(2)),
    p('output', 'text', 'standard', 5, 'tokens', later(10)),
    p('cache', 'all', 'standard', 0.1, 'tokens', later(0.2)),
    p('storage', 'all', 'standard', 0.5, 'tokens_hour', later(1)),
    p('input', 'all', 'batch', 0.5, 'tokens', later(1)),
    p('output', 'text', 'batch', 2.5, 'tokens', later(5)),
    p('cache', 'all', 'batch', 0.05, 'tokens', later(0.1)),
  ], { status: 'preview' }),
  model('veo-3.1-generate-preview', 'Veo 3.1', 'video', false, [
    p('output', 'video', 'standard', 0.4, 'second', { note: '720p / 1080p' }),
    p('output', 'video', 'standard', 0.6, 'second', { note: '4K' }),
  ], { status: 'preview' }),
  model('veo-3.1-fast-generate-preview', 'Veo 3.1 Fast', 'video', false, [
    p('output', 'video', 'standard', 0.1, 'second', { note: '720p' }),
    p('output', 'video', 'standard', 0.12, 'second', { note: '1080p' }),
    p('output', 'video', 'standard', 0.3, 'second', { note: '4K' }),
  ], { status: 'preview' }),
  model('veo-3.1-lite-generate-preview', 'Veo 3.1 Lite', 'video', false, [
    p('output', 'video', 'standard', 0.05, 'second', { note: '720p' }),
    p('output', 'video', 'standard', 0.08, 'second', { note: '1080p' }),
  ], { status: 'preview' }),
  model('lyria-3.5', 'Lyria 3.5', 'music', false, [
    p('output', 'audio', 'standard', 0.08, 'song'),
  ]),
]

/** Price in force on `date` (YYYY-MM-DD): switches to nextPrice once nextPriceFrom is reached */
export const effectivePrice = (row, date) =>
  row.nextPrice != null && row.nextPriceFrom && date >= row.nextPriceFrom ? Number(row.nextPrice) : Number(row.price)

/**
 * Cost of one request on a model. Uses the per-1M-token Standard/Batch input and output rows,
 * picks the ≤200k / >200k tier from the prompt size, and prefers the row matching `modality`.
 */
export const estimateCost = (model, { inputTokens = 0, outputTokens = 0, cachedTokens = 0, mode = 'standard', modality = 'text', date }) => {
  const tier = inputTokens + cachedTokens > 200000 ? 'gt200k' : 'lte200k'
  const pick = (kind) => {
    const rows = (model?.prices || []).filter((r) =>
      r.kind === kind && r.mode === mode && r.unit === 'tokens' && (r.condition === 'all' || r.condition === tier))
    const matches = (r) => r.modality === modality || r.modality.split('/').includes(modality)
    return rows.find(matches) || rows.find((r) => r.modality === 'all') || rows[0] || null
  }
  const line = (kind, tokens) => {
    const row = pick(kind)
    const rate = row ? effectivePrice(row, date) : null
    return { row, rate, tokens, cost: rate == null ? null : (tokens / 1e6) * rate }
  }
  const input = line('input', inputTokens)
  const output = line('output', outputTokens)
  const cache = line('cache', cachedTokens)
  const total = [input, output, cache].reduce((sum, l) => sum + (l.cost || 0), 0)
  return { input, output, cache, total, tier }
}
